import React, { useState, useRef, useEffect } from 'react'
import { SvgCross } from '../assets/images'
import { ShortcutKeys } from '../utils/tab-indexing-contants'
import {
  FIELD_VALUE_CONSTANTS,
  PopoverProps,
  POSITION_CONSTANTS,
} from '../utils'

const Popover: React.FC<PopoverProps> = ({
  children,
  content,
  title,
  cssClassName,
  showCloseButton = true,
  position = POSITION_CONSTANTS.AUTO,
  isOpen: controlledIsOpen,
  onClose,
  buttonRef,
  contentClassName,
  closeOnOutsideClick = true,
  buttonFocusable = true,
  customStyles,
}) => {
  const [isOpenInternal, setIsOpenInternal] = useState<boolean>(false)
  const isControlled = controlledIsOpen !== undefined
  const isOpen = isControlled ? controlledIsOpen : isOpenInternal
  const [dialogPosition, setDialogPosition] = useState<
    keyof typeof positionClasses
  >(POSITION_CONSTANTS.AUTO as keyof typeof positionClasses)
  const dialogRef = useRef<HTMLDialogElement | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const positionClasses: Record<
    (typeof POSITION_CONSTANTS)[keyof typeof POSITION_CONSTANTS],
    string
  > = {
    top: 'bottom-full mb-2',
    bottom: 'top-full mt-2',
    left: 'right-full mr-2',
    right: 'left-full ml-2',
    over: 'top-0 left-0 transform -translate-x-1/2 -translate-y-full mt-2',
    auto: 'position-auto',
    unset: 'bottom-0 right-0',
  }

  const toggleDialog = () => {
    if (!isControlled) {
      setIsOpenInternal((prev) => !prev)
    }
  }

  const closeDialog = () => {
    if (!isControlled) {
      setIsOpenInternal(false)
    }
    if (onClose) {
      onClose()
    }
  }

  const calculateFieldModalPosition = () => {
    if (buttonRef?.current && dialogRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      const dialogStyles = window.getComputedStyle(dialogRef.current)
      const dialogWidth = Math.min(
        viewportWidth * 0.98,
        Math.max(viewportWidth * 0.38, parseFloat(dialogStyles.width))
      )
      const dialogHeight = Math.min(
        parseFloat(dialogStyles.maxHeight || '820px'),
        viewportHeight
      )
      const bottomMargin = FIELD_VALUE_CONSTANTS.SIXTEEN
      let bottomPosition = viewportHeight - buttonRect.bottom
      if (bottomPosition + dialogHeight > viewportHeight) {
        bottomPosition = viewportHeight - dialogHeight
      }

      let leftPosition = buttonRect.right
      if (leftPosition + dialogWidth > viewportWidth) {
        leftPosition = viewportWidth - dialogWidth
      }

      dialogRef.current.style.position = 'fixed'
      dialogRef.current.style.bottom = `${Math.max(
        0,
        bottomPosition + bottomMargin
      )}px`
      dialogRef.current.style.left = `${Math.max(0, leftPosition)}px`
      dialogRef.current.style.width = `${dialogWidth}px`
      dialogRef.current.style.maxHeight = `${dialogHeight}px`
    }
  }

  const calculatePosition = () => {
    if (buttonRef?.current && dialogRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect()
      const dialogRect = dialogRef.current.getBoundingClientRect()
      const spaceAbove = buttonRect.top
      const spaceBelow = window.innerHeight - buttonRect.bottom
      const spaceLeft = buttonRect.left
      const spaceRight = window.innerWidth - buttonRect.right

      if (spaceBelow >= dialogRect.height) {
        setDialogPosition('bottom')
      } else if (spaceAbove >= dialogRect.height) {
        setDialogPosition('top')
      } else if (spaceRight >= dialogRect.width) {
        setDialogPosition('right')
      } else if (spaceLeft >= dialogRect.width) {
        setDialogPosition('left')
      } else {
        setDialogPosition('bottom')
      }
    }
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const dropdownRoot = document.getElementById('dropdown-root')
      if (
        closeOnOutsideClick &&
        dialogRef.current &&
        !dialogRef.current.contains(e.target as Node) &&
        ((buttonRef?.current &&
          !buttonRef?.current.contains(e.target as Node)) ||
          !buttonRef?.current) &&
        !dropdownRoot?.contains(e.target as Node)
      ) {
        closeDialog()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, closeOnOutsideClick])

  useEffect(() => {
    const dropdownRoot = document.getElementById('dropdown-root')

    const checkFocusTarget = () => {
      const active = document.activeElement

      if (
        isOpen &&
        active &&
        dialogRef.current &&
        !dialogRef.current.contains(active) &&
        !dropdownRoot?.contains(active)
      ) {
        closeDialog()
      }
    }

    const handleFocusIn = () => {
      setTimeout(checkFocusTarget, 0)
    }

    const handleWindowBlur = () => {
      setTimeout(() => {
        const active = document.activeElement

        if (
          isOpen &&
          (!active ||
            (dialogRef.current &&
              !dialogRef.current.contains(active) &&
              !dropdownRoot?.contains(active)))
        ) {
          closeDialog()
        }
      }, 0)
    }

    document.addEventListener('focusin', handleFocusIn)
    window.addEventListener('blur', handleWindowBlur)

    return () => {
      document.removeEventListener('focusin', handleFocusIn)
      window.removeEventListener('blur', handleWindowBlur)
    }
  }, [isOpen, closeOnOutsideClick])

  useEffect(() => {
    const updatePosition = () => {
      if (!isOpen) return

      if (position === 'auto') {
        calculatePosition()
      } else if (position === 'unset') {
        calculateFieldModalPosition()
      }
    }

    updatePosition()

    const handleResize = () => {
      updatePosition()
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isOpen, position])

  return (
    <div ref={popoverRef}>
      {children && (
        <button
          onClick={toggleDialog}
          ref={buttonRef}
          tabIndex={buttonFocusable ? 0 : -1}
        >
          {children}
        </button>
      )}
      {isOpen && (
        <dialog
          ref={dialogRef}
          open
          className={`absolute z-40 p-6 bg-accent-0 border border-accent-200 rounded-md shadow-lg max-w-md mx-auto ${
            positionClasses[dialogPosition]
          } ${cssClassName ?? ''}`}
          style={customStyles}
        >
          {title || showCloseButton ? (
            <div className="flex justify-between items-center bg-accent-50 h-10 pl-5 pr-1 py-4 rounded shadow">
              {title && (
                <div className="font-primary font-light text-sm">{title}</div>
              )}
              {showCloseButton && (
                <div
                  className="top-2 right-2 cursor-pointer p-1"
                  onClick={closeDialog}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (
                      e.key === ShortcutKeys.Enter ||
                      e.key === ShortcutKeys.Space
                    ) {
                      closeDialog()
                      e.preventDefault()
                    }
                  }}
                >
                  <span className="text-black">
                    <SvgCross />
                  </span>
                </div>
              )}
            </div>
          ) : null}
          <div
            className={`${title || showCloseButton ? 'mt-4' : ''} ${
              contentClassName ?? ''
            }`}
          >
            {content}
          </div>
        </dialog>
      )}
    </div>
  )
}

export default Popover
